// @ts-nocheck
import React from 'react';
import {
  BuildingStorefrontIcon,
  CalendarDaysIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { Modal } from '@/components/elements/Modal';
import {
  ExpandableCard,
  ExpandablePanel,
} from '@/components/elements/ExpandablePanel';
import PillCell from '@/components/organism/StatusPills/PillCell';
import { getDateTime } from '@/utils/dateTime';
import { Text, Caption } from '@/components';
import { PRODUCTS_PROPS } from '@/constant/product/product.const';
import { APP_BORDER } from '@/constant/theme/colors.constant';
import { PRODUCTS_TEST_IDS as TID } from '@/constant/testIds/products.testIds';
import { tid } from '@/utils/testId';

// ─── Props ────────────────────────────────────────────────────────────────────
interface ProductMarketDetailsDialogProps {
  marketDetails: any;
  openProductMarketDialog: boolean;
  handleProductMarketDialogOpen: () => void;
}

// ─── Dialog ───────────────────────────────────────────────────────────────────
const ProductMarketDetailsDialog: React.FC<ProductMarketDetailsDialogProps> = ({
  marketDetails,
  openProductMarketDialog,
  handleProductMarketDialogOpen,
}) => {
  const markets: any[] = marketDetails?.markets ?? [];
  const activeCount = markets.filter((m) => !m.deletedOn).length;
  const deletedCount = markets.filter((m) => m.deletedOn).length;

  const toolbar = (
    <div
      className={`flex items-center gap-2 px-4 pt-3 pb-2 border-b ${APP_BORDER.soft}`}
    >
      <PillCell
        value={`${activeCount} Active`}
        preset="boolEnabled"
        dot
        size="xs"
      />
      {deletedCount > 0 && (
        <PillCell
          value={`${deletedCount} Deactivated`}
          preset="inactive"
          dot
          size="xs"
        />
      )}
    </div>
  );

  return (
    <Modal
      open={openProductMarketDialog}
      onClose={handleProductMarketDialogOpen}
      size="2xl"
    >
      <Modal.Header
        title={PRODUCTS_PROPS.DIALOGS.MARKET_TITLE}
        subtitle={`${markets.length} market${markets.length !== 1 ? 's' : ''} · ${activeCount} active · ${deletedCount} deactivated`}
        icon={<BuildingStorefrontIcon className="w-4 h-4" />}
        onClose={handleProductMarketDialogOpen}
      />
      <Modal.Body noPadding>
        <ExpandablePanel
          items={markets}
          getItemId={(m) => String(m.marketId)}
          toolbar={toolbar}
          emptyIcon={<BuildingStorefrontIcon />}
          emptyTitle={PRODUCTS_PROPS.DIALOGS.NO_MARKETS_TITLE}
          emptyDescription={PRODUCTS_PROPS.DIALOGS.NO_MARKETS_DESC}
          renderItem={(m, expanded, onToggle) => {
            const isDeleted = Boolean(m?.deletedOn);
            const rowId = String(m?.marketId ?? m?.marketName ?? 'unknown');
            return (
              <ExpandableCard
                key={m.marketId}
                expanded={expanded}
                onToggle={onToggle}
                accentColor={isDeleted ? '#ef4444' : '#3b82f6'}
                showChevron={false}
                testId={tid(TID.DIALOG_MARKET_CARD(rowId))}
                header={
                  <div className="flex items-center gap-3 px-4 py-3">
                    <div
                      className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center ${
                        isDeleted
                          ? 'bg-gradient-to-br from-red-400 to-red-600'
                          : 'bg-gradient-to-br from-blue-400 to-blue-600'
                      }`}
                    >
                      <span className="text-white [&>*]:w-4 [&>*]:h-4">
                        <BuildingStorefrontIcon />
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <Text
                        as="p"
                        size="lg"
                        weight="bold"
                        color="default"
                        truncate
                        data-testid={tid(TID.DIALOG_MARKET_NAME(rowId))}
                      >
                        {m.marketName ?? `Market ${m.marketId}`}
                      </Text>
                    </div>
                    <div className="flex-shrink-0 hidden sm:flex flex-col items-end text-right">
                      <Caption size="sm" color="subtle">
                        {isDeleted ? 'Deactivated' : 'Last Sync'}
                      </Caption>
                      <Text
                        as="p"
                        size="md"
                        weight="semibold"
                        color={isDeleted ? 'danger' : 'soft'}
                        className="flex items-center gap-1 mt-0.5"
                        data-testid={tid(TID.DIALOG_MARKET_STATUS(rowId))}
                      >
                        {isDeleted ? (
                          <>
                            <TrashIcon className="w-3 h-3" />
                            {getDateTime(m.deletedOn)}
                          </>
                        ) : (
                          <>
                            <CalendarDaysIcon className="w-3 h-3 text-primary" />
                            {getDateTime(m.updatedOn)}
                          </>
                        )}
                      </Text>
                    </div>
                    <PillCell
                      value={isDeleted ? 'Deactivated' : 'Active'}
                      preset="boolEnabled"
                      dot
                      size="xs"
                    />
                  </div>
                }
              />
            );
          }}
        />
      </Modal.Body>
    </Modal>
  );
};

export default ProductMarketDetailsDialog;
